import {Helmet} from '@dr.pogodin/react-helmet';
import {
  Button,
  ButtonGroup,
  Card,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {Grid, Space, VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';

import {
  useAccGetConfigurationExportAbraQuery,
  useAccPatchConfigurationExportAbraMutation,
  useGetAccountingDictionaryQuery,
} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

import {abraFormSchema, AbraFormValues} from './abraFormSchema';

export function AccountingIntegrationsAbra() {
  const navigate = useNavigate();

  const {
    data: abraConfig,
    isLoading: isConfigLoading,
    isError: isConfigError,
  } = useAccGetConfigurationExportAbraQuery();

  const {
    data: mappingMethodDictionary,
    isLoading: isDictionaryLoading,
    isError: isDictionaryError,
  } = useGetAccountingDictionaryQuery({category: 'abra-mapping-method'});

  const [patchAbraConfig] = useAccPatchConfigurationExportAbraMutation();

  const mappingMethodOptions: {value: string; label: string}[] =
    mappingMethodDictionary
      ?.map((item) =>
        isNotNil(item.code) && isNotNil(item.name) ? {value: item.code, label: item.name} : null
      )
      .filter(isNotNil) ?? [];

  const defaultValues: AbraFormValues = {
    abraAccount: abraConfig?.abraAccount?.value ?? '',
    abraCompany: abraConfig?.abraCompany?.value ?? '',
    mappingMethod: abraConfig?.mappingMethod?.id ?? '',
    username: abraConfig?.abraCredentials?.username ?? '',
    password: abraConfig?.abraCredentials?.password ?? '',
  };

  const title = i18n.t('page.accountingSettings.labels.abraFlexi');
  const breadcrumbs = [
    {
      label: i18n.t('page.accountingSettings.labels.integrations'),
      href: settingsRoutes.accountingIntegrations,
    },
  ];

  const handleSubmit: FormSubmitHandler<AbraFormValues> = (
    values,
    _setErrors,
    _reset,
    blockerContext
  ) =>
    patchAbraConfig({
      body: {
        resource: {
          abraAccount: values.abraAccount,
          abraCompany: values.abraCompany,
          mappingMethod: values.mappingMethod,
        },
        credentials: {
          username: values.username,
          password: values.password,
        },
      },
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.accountingIntegrations);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );

  return (
    <>
      <Helmet title={title} />
      <SettingsTemplate
        header={{title, breadcrumbs}}
        isLoading={isConfigLoading || isDictionaryLoading}
        isError={isConfigError || isDictionaryError}
        data-testid={testIds.settings.accountingIntegrationsAbra('settings-template')}
      >
        <Card>
          <Form<AbraFormValues>
            onSubmit={handleSubmit}
            experimentalZodSchema={abraFormSchema}
            defaultValues={defaultValues}
            shouldWatchForUnsavedChanges
          >
            {(control, _formApi, navigateWithoutBlocker) => (
              <VStack spacing={4}>
                <Grid columns={2}>
                  <FormField
                    control={control}
                    type="text"
                    name="abraAccount"
                    label={i18n.t('page.accountingSettings.labels.instance')}
                    isRequired
                    data-testid={testIds.settings.accountingIntegrationsAbra('abraAccount')}
                  />
                  <FormField
                    control={control}
                    type="text"
                    name="abraCompany"
                    label={i18n.t('page.accountingSettings.labels.companyId')}
                    isRequired
                    data-testid={testIds.settings.accountingIntegrationsAbra('abraCompany')}
                  />
                  <FormField
                    control={control}
                    type="choice"
                    name="mappingMethod"
                    options={mappingMethodOptions}
                    label={i18n.t('page.accountingSettings.labels.mappingMethod')}
                    isRequired
                    isNotClearable
                    data-testid={testIds.settings.accountingIntegrationsAbra('mappingMethod')}
                  />
                  <FormField
                    control={control}
                    type="text"
                    name="username"
                    label={i18n.t('general.labels.username')}
                    isRequired
                    data-testid={testIds.settings.accountingIntegrationsAbra('username')}
                  />
                  <FormField
                    control={control}
                    type="password"
                    name="password"
                    label={i18n.t('general.labels.password')}
                    isRequired
                    data-testid={testIds.settings.accountingIntegrationsAbra('password')}
                  />
                </Grid>

                <Space vertical={2} />

                <ButtonGroup align="right">
                  <Button
                    title={i18n.t('general.actions.cancel')}
                    variant="secondary"
                    onClick={() => navigateWithoutBlocker(settingsRoutes.accountingIntegrations)}
                    data-testid={testIds.settings.accountingIntegrationsAbra('cancel')}
                  />
                  <FormButton
                    control={control}
                    type="submit"
                    title={i18n.t('general.actions.save')}
                    data-testid={testIds.settings.accountingIntegrationsAbra('save')}
                  />
                </ButtonGroup>
              </VStack>
            )}
          </Form>
        </Card>
      </SettingsTemplate>
    </>
  );
}
